/**
 * FEATURE PAGE: Strategy Score
 * Dedicated SEO-optimized landing page for the Strategy Score feature.
 * Route: /tools/strategy-score
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowRight, Target, TrendingUp, BarChart2,
  CheckCircle, Zap, Eye, Brain, Shield
} from 'lucide-react';

const SCORE_DIMENSIONS = [
  { label: 'Content Strategy', score: 72, color: 'from-amber-500 to-orange-500', icon: '📝' },
  { label: 'Competitive Moat', score: 58, color: 'from-red-500 to-rose-500', icon: '🛡️' },
  { label: 'Distribution Reach', score: 81, color: 'from-blue-500 to-cyan-500', icon: '📡' },
  { label: 'Conversion Engine', score: 44, color: 'from-yellow-400 to-amber-500', icon: '🎯' },
  { label: 'Brand Authority', score: 63, color: 'from-purple-500 to-violet-600', icon: '👑' },
  { label: 'Growth Velocity', score: 77, color: 'from-green-500 to-emerald-600', icon: '🚀' },
];

const ScoreBar = ({ score, color }) => (
  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
    <div
      className={`h-full bg-gradient-to-r ${color} rounded-full transition-all duration-1000`}
      style={{ width: `${score}%` }}
    />
  </div>
);

const FeaturePageStrategyScore = () => {
  return (
    <div className="min-h-screen bg-[#030305] text-white">
      {/* Ambient BG */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-amber-600/4 rounded-full blur-[220px]" />
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[600px] bg-blue-600/4 rounded-full blur-[220px]" />
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
      </div>

      <div className="relative z-10">
        {/* Nav */}
        <nav className="flex items-center justify-between max-w-6xl mx-auto px-6 py-5">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="font-bold text-white">iSupplyAI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/tools/living-war-room" className="text-sm text-gray-400 hover:text-white transition-colors">War Room</Link>
            <Link to="/strategy-score" className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-sm font-semibold text-black hover:opacity-90 transition-opacity">
              Get My Score
            </Link>
          </div>
        </nav>

        {/* Hero */}
        <section className="max-w-5xl mx-auto px-6 pt-16 pb-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-6">
            <Target className="w-3 h-3" /> AI-Powered Diagnosis
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] mb-6">
            Your Strategy
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-red-400">Score Exposed</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Paste your website URL. In 90 seconds, our AI council grades your strategy across 6 dimensions and tells you exactly what's costing you growth.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/strategy-score"
              className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-black font-bold text-base hover:from-amber-400 hover:to-orange-500 transition-all shadow-xl shadow-amber-500/20"
              data-testid="score-cta-hero"
            >
              <Target className="w-5 h-5" /> Score My Strategy Free
            </Link>
            <Link
              to="/tools/living-war-room"
              className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-gray-800/60 border border-gray-700/50 hover:border-amber-500/30 text-white text-base transition-all"
            >
              See the War Room <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-xs text-gray-600 mt-5">Free. No signup required. Results in 90 seconds.</p>
        </section>

        {/* Score preview */}
        <section className="max-w-4xl mx-auto px-6 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Mock score card */}
            <div className="rounded-2xl border border-amber-500/20 bg-gray-900/80 overflow-hidden shadow-2xl shadow-amber-500/10">
              <div className="px-5 py-4 border-b border-amber-500/10 bg-gradient-to-r from-amber-900/20 to-gray-900/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider">Overall Strategy Score</p>
                    <p className="text-4xl font-black text-white mt-1">66<span className="text-lg text-gray-500 font-normal">/100</span></p>
                  </div>
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                </div>
                <p className="text-xs text-amber-400/70 mt-2">Significant gaps identified in 2 critical areas</p>
              </div>
              <div className="p-5 space-y-4">
                {SCORE_DIMENSIONS.map((d) => (
                  <div key={d.label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-400 flex items-center gap-1.5">{d.icon} {d.label}</span>
                      <span className="text-xs font-bold text-white">{d.score}</span>
                    </div>
                    <ScoreBar score={d.score} color={d.color} />
                  </div>
                ))}
              </div>
              <div className="px-5 pb-5">
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-xs text-red-400 font-semibold mb-1">Top Priority</p>
                  <p className="text-xs text-gray-400">Your conversion engine score of 44 is the primary growth inhibitor. Fix this before scaling ad spend.</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-black mb-6">A full diagnosis, not a vague scorecard</h2>
              <div className="space-y-5">
                {[
                  { icon: <Brain className="w-4 h-4 text-purple-400" />, title: '6-dimension analysis', desc: 'Content, competitive moat, distribution, conversion, brand authority, and growth velocity — all graded.' },
                  { icon: <TrendingUp className="w-4 h-4 text-green-400" />, title: 'Benchmarked against your industry', desc: 'Your score is compared to top performers in your category — not generic averages.' },
                  { icon: <Zap className="w-4 h-4 text-amber-400" />, title: 'Actionable, ranked fixes', desc: 'Every gap comes with a prioritized action item and estimated impact on growth.' },
                  { icon: <Eye className="w-4 h-4 text-blue-400" />, title: 'Powered by the Gods', desc: 'Athena, Hermes, and Apollo review the scan and debate the findings before you see them.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-800/60 border border-gray-700/40 flex items-center justify-center flex-shrink-0">{f.icon}</div>
                    <div>
                      <p className="text-sm font-semibold text-white">{f.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What you get */}
        <section className="max-w-5xl mx-auto px-6 pb-24">
          <h2 className="text-3xl font-black text-center mb-10">What's In Your Report</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <BarChart2 className="w-5 h-5 text-amber-400" />, title: 'Score Breakdown', desc: 'Visual breakdown of all 6 strategic dimensions with trend indicators.' },
              { icon: <Target className="w-5 h-5 text-red-400" />, title: 'Critical Gaps', desc: 'The exact 2–3 areas where you are losing the most growth potential right now.' },
              { icon: <Shield className="w-5 h-5 text-blue-400" />, title: 'Competitive Context', desc: 'How your score compares to the top 10% in your category.' },
              { icon: <Zap className="w-5 h-5 text-green-400" />, title: 'Priority Actions', desc: 'A ranked list of fixes ordered by impact-to-effort ratio.' },
              { icon: <Brain className="w-5 h-5 text-purple-400" />, title: 'God Consensus', desc: 'What Athena, Hermes, and Apollo collectively recommend as your first move.' },
              { icon: <TrendingUp className="w-5 h-5 text-cyan-400" />, title: '90-Day Forecast', desc: 'Projected score improvement if you execute the top 3 recommendations.' },
            ].map((item) => (
              <div key={item.title} className="bg-gray-900/60 border border-gray-700/40 rounded-xl p-5 hover:border-amber-500/20 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-gray-800/60 border border-gray-700/30 flex items-center justify-center mb-3">{item.icon}</div>
                <p className="text-sm font-bold text-white mb-1">{item.title}</p>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
          <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-b from-amber-900/15 to-gray-900/50 p-12">
            <h2 className="text-3xl font-black mb-4">What's your score?</h2>
            <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
              Most teams are surprised by what the score surfaces. Paste your URL and find out in 90 seconds.
            </p>
            <Link
              to="/strategy-score"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-black font-bold hover:from-amber-400 hover:to-orange-500 transition-all shadow-xl shadow-amber-500/20"
              data-testid="score-final-cta"
            >
              <Target className="w-5 h-5" /> Score My Strategy Free
            </Link>
            <div className="flex items-center justify-center gap-4 mt-5 text-xs text-gray-600">
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> No signup needed</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Results in 90 seconds</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Shareable report</span>
            </div>
          </div>
        </section>

        <div className="text-center pb-10 text-[10px] text-gray-700">
          iSupplyAI — 12 AI Gods. One Strategy. &nbsp;·&nbsp; <Link to="/tools/living-war-room" className="hover:text-gray-500">Living War Room</Link> &nbsp;·&nbsp; <Link to="/replays" className="hover:text-gray-500">Replays</Link>
        </div>
      </div>
    </div>
  );
};

export default FeaturePageStrategyScore;
